import React, { useEffect, useState } from "react";
import { Plus, Trash2, Eye, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useVariants, VariantSelect } from "../components/VariantPicker";

export default function Receiving() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/receivings").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const action = async (id, act) => {
    try {
      const r = await api.post(`/receivings/${id}/${act}`);
      toast.success(act === "confirm" ? `Dikonfirmasi. Batch QC: ${r.data.qc_batch}` : "Berhasil");
      load(); setDetail(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="receiving-page">
      <PageHeader title="Penerimaan" subtitle="Penerimaan barang dari konveksi/supplier">
        {can("warehouse_admin") && <Button onClick={() => setOpen(true)} data-testid="add-receiving-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Penerimaan</Button>}
      </PageHeader>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Supplier</TableHead>
              <TableHead>Gudang</TableHead><TableHead className="text-center">Item</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.id} data-testid={`receiving-row-${r.id}`}>
                <TableCell className="font-mono text-xs font-semibold text-cocoa">{r.number}</TableCell>
                <TableCell className="text-xs text-wmuted">{fmtDate(r.date)}</TableCell>
                <TableCell>{r.supplier || "-"}</TableCell>
                <TableCell>{r.warehouse_name}</TableCell>
                <TableCell className="text-center">{r.items?.length || 0}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`view-receiving-${r.id}`}><Eye className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10">Belum ada penerimaan.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <ReceivingDialog open={open} setOpen={setOpen} onDone={load} />
      <DetailDialog detail={detail} setDetail={setDetail} can={can} action={action} />
    </div>
  );
}

function ReceivingDialog({ open, setOpen, onDone }) {
  const { flat } = useVariants();
  const [warehouses, setWarehouses] = useState([]);
  const [header, setHeader] = useState({ supplier: "", delivery_note_no: "", warehouse_id: "", receiver: "", notes: "" });
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get("/warehouses").then((r) => setWarehouses(r.data)); }, [open]);

  const addItem = () => setItems([...items, { variant_id: "", qty_delivery_note: 0, qty_received: 0, notes: "" }]);
  const updItem = (i, k, v) => { const n = [...items]; n[i][k] = v; setItems(n); };

  const save = async () => {
    if (!header.warehouse_id) return toast.error("Pilih gudang penerima");
    const payload = {
      ...header,
      items: items.filter((it) => it.variant_id).map((it) => {
        const f = flat.find((x) => x.variant_id === it.variant_id);
        return { ...f, qty_delivery_note: parseInt(it.qty_delivery_note) || 0, qty_received: parseInt(it.qty_received) || 0, notes: it.notes };
      }),
    };
    if (!payload.items.length) return toast.error("Tambahkan minimal 1 item");
    try {
      await api.post("/receivings", payload);
      toast.success("Penerimaan dibuat (Draft)");
      setOpen(false); setHeader({ supplier: "", delivery_note_no: "", warehouse_id: "", receiver: "", notes: "" }); setItems([]);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Penerimaan Barang Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Supplier/Konveksi</Label><Input value={header.supplier} onChange={(e) => setHeader({ ...header, supplier: e.target.value })} data-testid="rcv-supplier-input" /></div>
          <div><Label>Nomor Surat Jalan</Label><Input value={header.delivery_note_no} onChange={(e) => setHeader({ ...header, delivery_note_no: e.target.value })} /></div>
          <div><Label>Gudang Penerima *</Label>
            <Select value={header.warehouse_id} onValueChange={(v) => setHeader({ ...header, warehouse_id: v })}>
              <SelectTrigger data-testid="rcv-warehouse-select"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Petugas Penerima</Label><Input value={header.receiver} onChange={(e) => setHeader({ ...header, receiver: e.target.value })} /></div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={header.notes} onChange={(e) => setHeader({ ...header, notes: e.target.value })} /></div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>Detail Barang</Label>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="rcv-add-item-btn" className="border-taupe text-cocoa">+ Item</Button>
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_90px_auto] gap-2 mb-2 items-center">
              <VariantSelect flat={flat} value={it.variant_id} onChange={(v) => updItem(i, "variant_id", v)} testId={`rcv-variant-${i}`} />
              <Input type="number" placeholder="Qty SJ" value={it.qty_delivery_note} onChange={(e) => updItem(i, "qty_delivery_note", e.target.value)} />
              <Input type="number" placeholder="Qty Fisik" value={it.qty_received} onChange={(e) => updItem(i, "qty_received", e.target.value)} data-testid={`rcv-qty-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-status-reject" /></Button>
            </div>
          ))}
          {!items.length && <div className="text-sm text-wmuted py-3">Belum ada item.</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-receiving-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan Draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ detail, setDetail, can, action }) {
  if (!detail) return null;
  return (
    <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-3">{detail.number} <StatusBadge status={detail.status} /></DialogTitle></DialogHeader>
        <div className="text-sm grid grid-cols-2 gap-2 mb-3">
          <div><span className="text-wmuted">Supplier:</span> {detail.supplier || "-"}</div>
          <div><span className="text-wmuted">Surat Jalan:</span> {detail.delivery_note_no || "-"}</div>
          <div><span className="text-wmuted">Gudang:</span> {detail.warehouse_name}</div>
          <div><span className="text-wmuted">Petugas:</span> {detail.receiver}</div>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-right">Qty SJ</TableHead><TableHead className="text-right">Qty Fisik</TableHead><TableHead className="text-right">Selisih</TableHead></TableRow></TableHeader>
          <TableBody>
            {detail.items.map((it, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium text-cocoa">{it.product_name}</TableCell>
                <TableCell>{it.color}/{it.size}</TableCell>
                <TableCell className="text-right">{it.qty_delivery_note}</TableCell>
                <TableCell className="text-right font-semibold">{it.qty_received}</TableCell>
                <TableCell className={`text-right ${it.diff !== 0 ? "text-status-rework font-bold" : ""}`}>{it.diff}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {can("warehouse_admin") && detail.status === "Draft" && (
          <DialogFooter>
            <Button variant="outline" onClick={() => action(detail.id, "cancel")} className="border-status-reject text-status-reject"><XCircle className="w-4 h-4 mr-1.5" />Batalkan</Button>
            <Button onClick={() => action(detail.id, "confirm")} data-testid="confirm-receiving-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><CheckCircle2 className="w-4 h-4 mr-1.5" />Konfirmasi & Buat QC</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
